import React from "react";
import { Box, Paper, Typography, IconButton, alpha } from "@mui/material";
import { BackspaceOutlined, SpaceBar, KeyboardHide } from "@mui/icons-material";

/**
 * POSKeyboard — compact touch-friendly keyboard for POS search fields.
 *
 * Props:
 *   value       {string}   – controlled value
 *   onChange    {fn}       – called with new string whenever a key is pressed
 *   onClose     {fn}       – called when the hide button is pressed (optional)
 *   numericOnly {bool}     – show only the numpad, no QWERTY (optional)
 */

const QWERTY_ROWS = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M"],
];

const NUM_ROW = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

const NUMPAD_ROWS = [
  ["7", "8", "9"],
  ["4", "5", "6"],
  ["1", "2", "3"],
  [".", "0", "⌫"],
];

const POSKeyboard = ({
  value = "",
  onChange,
  onClose,
  onEnter,
  numericOnly = false,
}) => {
  const accent = "#667eea";
  const red = "#ef4444";
  const keyBg = "#1e2433";
  const keyBgHover = "#2d3550";
  const keyBorder = alpha("#fff", 0.09);
  const panelBg = "#141929";

  const emit = (newVal) => onChange && onChange(newVal);
  const handleKey = (k) => {
    if (k === "⌫") emit(value.slice(0, -1));
    else emit(value + k);
  };
  const handleBackspace = () => emit(value.slice(0, -1));
  const handleClear = () => emit("");
  const handleSpace = () => emit(value + " ");

  // ── Key style helpers ─────────────────────────────────────────────────────
  const keyStyle = (extra = {}) => ({
    height: 52,
    bgcolor: keyBg,
    border: `1px solid ${keyBorder}`,
    borderRadius: "10px",
    color: "white",
    fontSize: "1.3rem",
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
    transition: "all 0.09s ease",
    boxShadow: `0 3px 7px ${alpha("#000", 0.45)}, inset 0 1px 0 ${alpha("#fff", 0.06)}`,
    "&:hover": { bgcolor: keyBgHover, transform: "scale(1.05)" },
    "&:active": {
      bgcolor: accent,
      transform: "scale(0.93)",
      boxShadow: `0 0 16px ${alpha(accent, 0.7)}`,
    },
    ...extra,
  });

  const specialStyle = (color = red, extra = {}) => ({
    ...keyStyle(extra),
    bgcolor: alpha(color, 0.18),
    border: `1px solid ${alpha(color, 0.4)}`,
    fontSize: "1.1rem",
    fontWeight: 800,
    "&:hover": { bgcolor: alpha(color, 0.3) },
    "&:active": { bgcolor: color, transform: "scale(0.93)" },
  });

  // ── Numpad (right side panel or standalone) ───────────────────────────────
  const NumPad = ({ standalone = false }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        ...(standalone ? {} : { width: 188, flexShrink: 0 }),
      }}
    >
      {NUMPAD_ROWS.map((row, ri) => (
        <Box key={ri} sx={{ display: "flex", gap: 0.75 }}>
          {row.map((k) => (
            <Box
              key={k}
              sx={{
                ...(k === "⌫" ? specialStyle(red) : keyStyle()),
                flex: 1,
                height: standalone ? 70 : 52,
                fontSize: k === "⌫" ? undefined : "1.5rem",
              }}
              onClick={() => handleKey(k)}
            >
              {k === "⌫" ? <BackspaceOutlined sx={{ fontSize: 24 }} /> : k}
            </Box>
          ))}
        </Box>
      ))}
      {/* Clear button */}
      <Box
        sx={{
          ...specialStyle(red),
          height: standalone ? 54 : 46,
          letterSpacing: "1px",
        }}
        onClick={handleClear}
      >
        CLEAR
      </Box>
      {/* Enter button — shown whenever onEnter is provided */}
      {onEnter && (
        <Box
          sx={{
            ...specialStyle(accent),
            height: standalone ? 62 : 52,
            letterSpacing: "1px",
            fontSize: "1.1rem",
            flexDirection: "column",
            gap: 0.25,
          }}
          onClick={onEnter}
        >
          ↵
          <Box
            component="span"
            sx={{
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "1.5px",
            }}
          >
            ENTER
          </Box>
        </Box>
      )}
    </Box>
  );

  // ── Full QWERTY + Numpad side-by-side ─────────────────────────────────────
  const FullBoard = () => (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      {/* ── QWERTY side ──────────────────────────────── */}
      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}
      >
        {/* Number row */}
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {NUM_ROW.map((k) => (
            <Box
              key={k}
              sx={{ ...keyStyle(), flex: 1 }}
              onClick={() => handleKey(k)}
            >
              {k}
            </Box>
          ))}
          <Box
            sx={{ ...specialStyle(red), width: 60, flexShrink: 0, height: 52 }}
            onClick={handleBackspace}
          >
            <BackspaceOutlined sx={{ fontSize: 24 }} />
          </Box>
        </Box>

        {/* Q W E R … rows */}
        {/* Row 0: QWERTYUIOP */}
        <Box sx={{ display: "flex", gap: 0.75, justifyContent: "center" }}>
          {QWERTY_ROWS[0].map((k) => (
            <Box
              key={k}
              sx={{ ...keyStyle(), flex: 1, minWidth: 40 }}
              onClick={() => handleKey(k)}
            >
              {k}
            </Box>
          ))}
        </Box>

        {/* Rows 1+2 with tall ENTER on the right */}
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "stretch" }}>
          {/* Left column: ASDFGHJKL + ZXCVBNM stacked */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {/* Row 1: ASDFGHJKL */}
            <Box sx={{ display: "flex", gap: 0.75 }}>
              <Box sx={{ width: 26, flexShrink: 0 }} />
              {QWERTY_ROWS[1].map((k) => (
                <Box
                  key={k}
                  sx={{ ...keyStyle(), flex: 1, minWidth: 40 }}
                  onClick={() => handleKey(k)}
                >
                  {k}
                </Box>
              ))}
            </Box>
            {/* Row 2: ZXCVBNM */}
            <Box sx={{ display: "flex", gap: 0.75 }}>
              <Box sx={{ width: 56, flexShrink: 0 }} />
              {QWERTY_ROWS[2].map((k) => (
                <Box
                  key={k}
                  sx={{ ...keyStyle(), flex: 1, minWidth: 40 }}
                  onClick={() => handleKey(k)}
                >
                  {k}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Tall ENTER key spanning both rows */}
          <Box
            sx={{
              ...specialStyle(accent),
              width: 84,
              flexShrink: 0,
              alignSelf: "stretch",
              flexDirection: "column",
              gap: 0.25,
              fontSize: "1.8rem",
              borderRadius: "12px",
            }}
            onClick={onEnter}
          >
            ↵
            <Box
              component="span"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "1.5px",
                mt: 0.25,
              }}
            >
              ENTER
            </Box>
          </Box>
        </Box>

        {/* Space row */}
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <Box
            sx={{ ...specialStyle(red), width: 96, flexShrink: 0 }}
            onClick={handleClear}
          >
            CLEAR
          </Box>
          <Box sx={{ ...keyStyle(), flex: 1 }} onClick={handleSpace}>
            <SpaceBar sx={{ fontSize: 28 }} />
          </Box>
          <Box
            sx={{ ...specialStyle(red), width: 96, flexShrink: 0 }}
            onClick={handleBackspace}
          >
            <BackspaceOutlined sx={{ fontSize: 24 }} />
          </Box>
        </Box>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          bgcolor: alpha("#fff", 0.08),
          alignSelf: "stretch",
          borderRadius: 1,
          mx: 0.5,
        }}
      />

      {/* ── Numpad side ──────────────────────────────── */}
      <NumPad standalone={false} />
    </Box>
  );

  return (
    <Paper
      elevation={10}
      sx={{
        bgcolor: panelBg,
        borderRadius: "20px",
        p: 1.5,
        border: `1px solid ${alpha("#fff", 0.07)}`,
        boxShadow: `0 -8px 40px ${alpha("#000", 0.6)}, 0 0 0 1px ${alpha(accent, 0.2)}`,
        userSelect: "none",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, transparent 0%, ${accent} 50%, transparent 100%)`,
        },
      }}
    >
      {/* Top bar: value preview + close button */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        <Box
          sx={{
            flex: 1,
            bgcolor: alpha("#fff", 0.05),
            border: `1px solid ${alpha("#fff", 0.1)}`,
            borderRadius: "10px",
            px: 1.5,
            py: 0.75,
            minHeight: 38,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.05rem",
              fontWeight: 600,
              color: value ? "white" : alpha("#fff", 0.3),
              fontFamily: "monospace",
              letterSpacing: "0.5px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {value || "Start typing…"}
          </Typography>
          <Box
            sx={{
              width: 2,
              height: 18,
              bgcolor: accent,
              ml: 0.5,
              borderRadius: 1,
              flexShrink: 0,
              animation: "kbBlink 1s step-end infinite",
              "@keyframes kbBlink": {
                "0%,100%": { opacity: 1 },
                "50%": { opacity: 0 },
              },
            }}
          />
        </Box>

        {onClose && (
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: alpha("#fff", 0.5),
              bgcolor: alpha("#fff", 0.05),
              border: `1px solid ${alpha("#fff", 0.09)}`,
              borderRadius: "10px",
              width: 40,
              height: 40,
              flexShrink: 0,
              "&:hover": { bgcolor: alpha(red, 0.22), color: red },
            }}
          >
            <KeyboardHide sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      {/* Keyboard body */}
      {numericOnly ? <NumPad standalone /> : <FullBoard />}
    </Paper>
  );
};

export default POSKeyboard;
